import { Platform } from 'react-native';

/**
 * Get the API base URL for constructing proxy URLs
 */
let cachedApiBaseURL: string | null = null;

export const getApiBaseURL = async (): Promise<string | null> => {
  if (cachedApiBaseURL) {
    return cachedApiBaseURL;
  }

  try {
    const { getCurrentApiConfig } = await import('../config/api.config');
    const apiConfig = getCurrentApiConfig();
    const baseURL = apiConfig.baseURL?.trim();
    if (baseURL) {
      cachedApiBaseURL = baseURL;
      return baseURL;
    }
  } catch (error) {
    console.error('[imageProxy] Failed to load API config:', error);
  }

  // Fallback to environment variable or window location
  if (typeof window !== 'undefined' && window.location) {
    const fallbackURL = `${window.location.protocol}//${window.location.host}`;
    cachedApiBaseURL = fallbackURL;
    return fallbackURL;
  }

  const fallbackURL = (process.env.API_BASE_URL || 'https://api.connect.dislix.com').trim();
  cachedApiBaseURL = fallbackURL;
  return fallbackURL;
};

const PROXY_PATH = '/api/storage/media/proxy';

/**
 * Stored URLs are sometimes already proxy URLs (on a retired API host, or
 * wrapped twice). Unwrap them to the original file URL, keeping mimeType,
 * so they're proxied once through the current API (or loaded directly on
 * mobile) instead of going through the old host.
 */
const unwrapProxyUrl = (url: string): { source: string; mimeType?: string } => {
  let source = url;
  let mimeType: string | undefined;
  for (let depth = 0; depth < 3 && source.includes(`${PROXY_PATH}?`); depth++) {
    const params = new URLSearchParams(source.slice(source.indexOf('?') + 1));
    const inner = params.get('url');
    if (!inner) break;
    mimeType = mimeType || params.get('mimeType') || undefined;
    source = inner;
  }
  return { source, mimeType };
};

const buildProxyUrl = (baseURL: string, source: string, mimeType?: string): string =>
  `${baseURL}${PROXY_PATH}?url=${encodeURIComponent(source)}${mimeType ? `&mimeType=${encodeURIComponent(mimeType)}` : ''}`;

/**
 * Helper function to get image URL with proxy support for Firebase Storage URLs
 * Similar to how calendar attachments are handled
 * 
 * @param url - The original image URL
 * @param apiBaseURL - Optional API base URL (will be fetched if not provided)
 * @returns The proxied URL for Firebase Storage URLs on web, or original URL otherwise
 */
export const getImageUrl = async (url: string, apiBaseURL?: string | null): Promise<string> => {
  if (!url) return url;
  
  // Blob URLs should be used directly (they're temporary local references)
  if (url.startsWith('blob:')) {
    return url;
  }
  
  const { source, mimeType } = unwrapProxyUrl(url);

  // On web, ALWAYS use proxy endpoint for Firebase Storage URLs to avoid CORS issues
  // Similar to how calendar attachments are handled
  if (Platform.OS === 'web' && source.includes('firebasestorage.googleapis.com')) {
    const baseURL = apiBaseURL || await getApiBaseURL();
    if (baseURL) {
      return buildProxyUrl(baseURL, source, mimeType);
    }
    // If baseURL is not available, return original URL (may have CORS issues)
    console.warn('[imageProxy] API base URL not available, using original Firebase URL (may have CORS issues):', source);
    return source;
  }
  
  // For mobile or non-Firebase URLs, use the original file URL
  return source;
};

/**
 * Synchronous version that uses cached API base URL
 * Use this when you need the URL immediately and can't await
 */
export const getImageUrlSync = (url: string, apiBaseURL?: string | null): string => {
  if (!url) return url;
  
  // Blob URLs should be used directly
  if (url.startsWith('blob:')) {
    return url;
  }
  
  const { source, mimeType } = unwrapProxyUrl(url);

  // On web, use proxy endpoint for Firebase Storage URLs
  if (Platform.OS === 'web' && source.includes('firebasestorage.googleapis.com')) {
    const baseURL = apiBaseURL || cachedApiBaseURL;
    if (baseURL) {
      return buildProxyUrl(baseURL, source, mimeType);
    }
    // If baseURL is not available, return original URL
    return source;
  }
  
  // For mobile or non-Firebase URLs, use the original file URL
  return source;
};

